import React, { useRef, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { useFormContext } from 'react-hook-form';
import {
  ArrowSquareOut,
  CheckCircle,
  CloudArrowUp,
  FileDoc,
  Robot,
  WarningCircle,
} from 'phosphor-react';
import {
  IDadosContratoVigente,
  IIniciarAlteracaoResponse,
  useSocietario,
} from '../../../../../hooks/useSocietario';
import { useDialog } from '../../../../../providers/dialog';
import { extractApiErrorMessage } from '../../../../../utils/ErrorUtils';
import { isValidCNPJ } from '../../../../../utils/documentValidation';
import { IWizardFormValues } from '../schema';

// Limite de tamanho do upload — espelhado do helper text "ate 50MB" que ja
// aparecia na UI antes (mas nao era validado, e backend processava arquivo
// arbitrariamente grande).
const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;

// Sessao 2026-05-11 (item 9 alt Fase 5): heuristicas deterministicas pra
// flagar campos extraidos que MERECEM atencao (sem score de confianca da IA
// — checagens deterministicas baratas no front).
const isCpfFormatoValido = (v: string | null | undefined): boolean => {
  if (!v) return true; // sem CPF != invalido (operador preenche)
  const digits = v.replace(/\D/g, '');
  return digits.length === 11;
};

interface AvisosAtencao {
  /** Bloqueiam a geracao sem confirmacao explicita do operador (review Matias #8). */
  criticos: string[];
  /** Apenas alertam — fluxo segue normalmente. */
  informativos: string[];
}

function avaliarAtencoes(dados: IDadosContratoVigente): AvisosAtencao {
  const criticos: string[] = [];
  const informativos: string[] = [];
  if (dados.cnpj && !isValidCNPJ(dados.cnpj)) {
    criticos.push(`CNPJ extraído (${dados.cnpj}) tem dígito verificador inválido.`);
  }
  if (dados.capital_social_atual != null && dados.capital_social_atual <= 0) {
    criticos.push(`Capital extraído (R$ ${dados.capital_social_atual}) é zero ou negativo.`);
  }
  if (!dados.razao_social || !dados.razao_social.trim()) {
    criticos.push('Razão social não foi extraída — preencha manualmente no Step Empresa.');
  }
  if (dados.numero_ultima_alteracao != null && dados.numero_ultima_alteracao < 0) {
    informativos.push(
      `Número da última alteração extraído (${dados.numero_ultima_alteracao}) é negativo.`,
    );
  }
  const socios = dados.quadro_socios_atual ?? [];
  socios.forEach((s, i) => {
    if (!s.cpf && !s.cnpj) {
      informativos.push(`Sócio "${s.nome || `#${i + 1}`}" sem CPF/CNPJ extraído.`);
    } else if (s.cpf && !isCpfFormatoValido(s.cpf)) {
      criticos.push(`Sócio "${s.nome}" — CPF extraído (${s.cpf}) tem formato inválido.`);
    } else if (s.cnpj && !isValidCNPJ(s.cnpj)) {
      criticos.push(`Sócio "${s.nome}" — CNPJ extraído (${s.cnpj}) tem DV inválido.`);
    }
  });
  return { criticos, informativos };
}

/**
 * Sessao 2026-05-14 (review Matias PR #345 #8): exposicao dos avisos criticos
 * pro parent (criar/index.tsx). O handleGerar usa pra exibir confirmacao
 * antes de queimar IA com dados extraidos sabidamente invalidos.
 *
 * Aceita null/undefined porque o `contrato_vigente_dados_extraidos` pode nao
 * existir (constituicao, alteracao sem upload, falha de extracao IA).
 */
export function avaliarAtencoesCriticos(dados: IDadosContratoVigente | null | undefined): string[] {
  if (!dados) return [];
  return avaliarAtencoes(dados).criticos;
}

interface ContratoVigenteStepProps {
  /**
   * Disparado quando o upload + extracao concluem com sucesso. Parent usa
   * pra guardar o `contrato_id` no state externo (alem do form value) e
   * disparar o pre-preenchimento dos campos do Step Empresa quando o
   * usuario avancar para o proximo step.
   */
  // eslint-disable-next-line no-unused-vars
  onUploadSuccess: (response: IIniciarAlteracaoResponse) => void;
  /**
   * Dados extraidos JA persistidos no contrato (`createdContrato.
   * contrato_vigente_dados_extraidos`). Re-exibe a caixinha verde quando o
   * operador volta pra este step — o estado local `uploaded` se perde na
   * desmontagem. Null/undefined enquanto nao houve upload.
   */
  dadosExtraidosPersistidos?: IDadosContratoVigente | null;
}

// Formatadores reutilizaveis pro preview.
const formatCurrency = (v?: number | null): string => {
  if (v == null) return '—';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(v);
};

const Field = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Box>
    <Text fontSize="2xs" color="gray.500" textTransform="uppercase" letterSpacing="wide">
      {label}
    </Text>
    <Text fontSize="sm" fontWeight="medium" mt={0.5}>
      {value || (
        <Text as="span" color="gray.400">
          —
        </Text>
      )}
    </Text>
  </Box>
);

function renderUploadFeedback(
  uploaded: IIniciarAlteracaoResponse | null,
  dadosPersistidos: IDadosContratoVigente | null,
): React.ReactNode {
  if (uploaded?.extraction_error) {
    return (
      <Alert status="warning" borderRadius="md">
        <AlertIcon />
        <Box flex={1}>
          <Text fontSize="sm" fontWeight="medium">
            Extração automática não foi possível
          </Text>
          <Text fontSize="xs" color="gray.600" mt={1}>
            Erro: {uploaded.extraction_error}. O upload foi feito, mas você precisará preencher os
            próximos passos manualmente.
          </Text>
        </Box>
      </Alert>
    );
  }
  // Prioriza o upload desta sessao; cai pros dados ja persistidos no contrato
  // quando o operador volta pra este step (o estado local `uploaded` desmontou).
  const dados = uploaded?.dados_extraidos ?? dadosPersistidos;
  if (dados) {
    return <PreviewDadosExtraidos dados={dados} presignedUrl={uploaded?.presigned_url ?? null} />;
  }
  return (
    <Alert status="info" borderRadius="md">
      <Icon as={WarningCircle} mr={2} />
      <Text fontSize="sm">
        Contrato anexado em sessão anterior. Os próximos passos já foram pré-preenchidos.
      </Text>
    </Alert>
  );
}

const PreviewDadosExtraidos = ({
  dados,
  presignedUrl,
}: {
  dados: IDadosContratoVigente;
  presignedUrl: string | null;
}) => {
  // Sessao 2026-05-11 (item 9 alt Fase 5): atencoes deterministicas
  // ao lado dos campos. Sem score de confianca da IA — checagens
  // determinist­icas baratas no front.
  // Sessao 2026-05-14 (review Matias PR #345 #8): avisos categorizados em
  // criticos (CNPJ DV invalido, capital ≤ 0, razao vazia, CPF/CNPJ socio
  // invalido) vs informativos. Criticos exibem em vermelho e bloqueiam
  // /gerar com confirmacao explicita do parent.
  const { criticos, informativos } = avaliarAtencoes(dados);
  return (
    <Box bg="green.50" borderWidth="1px" borderColor="green.200" borderRadius="md" p={4}>
      <Flex justify="space-between" align="center" mb={3} flexWrap="wrap" gap={2}>
        <HStack>
          <Icon as={Robot} color="green.600" weight="duotone" boxSize={5} />
          <Text fontWeight="bold" fontSize="sm" color="green.800">
            Dados extraídos pela IA
          </Text>
          <Badge colorScheme="green" fontSize="2xs">
            revise antes de avançar
          </Badge>
        </HStack>
        {presignedUrl && (
          <Button
            size="xs"
            variant="outline"
            leftIcon={<ArrowSquareOut size={14} />}
            onClick={() => window.open(presignedUrl, '_blank', 'noopener,noreferrer')}
          >
            Abrir vigente em nova aba
          </Button>
        )}
      </Flex>

      {criticos.length > 0 && (
        <Alert status="error" borderRadius="md" mb={3} fontSize="xs" alignItems="flex-start">
          <AlertIcon mt={0.5} />
          <Box>
            <Text fontWeight="semibold" mb={1}>
              {criticos.length} item(ns) crítico(s) na extração:
            </Text>
            <VStack align="stretch" spacing={0.5}>
              {criticos.slice(0, 5).map(a => (
                <Text key={a}>• {a}</Text>
              ))}
              {criticos.length > 5 && <Text>• …e mais {criticos.length - 5}.</Text>}
            </VStack>
            <Text mt={1} color="gray.700">
              Corrija manualmente nos próximos passos. No momento de gerar, a IA vai pedir
              confirmação porque esses dados afetam a qualidade do contrato.
            </Text>
          </Box>
        </Alert>
      )}

      {informativos.length > 0 && (
        <Alert status="warning" borderRadius="md" mb={3} fontSize="xs" alignItems="flex-start">
          <AlertIcon mt={0.5} />
          <Box>
            <Text fontWeight="semibold" mb={1}>
              {informativos.length} ponto(s) de atenção:
            </Text>
            <VStack align="stretch" spacing={0.5}>
              {informativos.slice(0, 5).map(a => (
                <Text key={a}>• {a}</Text>
              ))}
              {informativos.length > 5 && <Text>• …e mais {informativos.length - 5}.</Text>}
            </VStack>
            <Text mt={1} color="gray.600" fontStyle="italic">
              Confira no PDF original e corrija manualmente nos próximos passos se necessário.
            </Text>
          </Box>
        </Alert>
      )}

      <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
        <Field label="Razão social" value={dados.razao_social} />
        <Field label="Nome fantasia" value={dados.nome_fantasia} />
        <Field label="CNPJ" value={dados.cnpj} />
        <Field label="NIRE" value={dados.nire} />
        <Field label="Capital social atual" value={formatCurrency(dados.capital_social_atual)} />
        <Field label="Última alteração nº" value={dados.numero_ultima_alteracao} />
        <Field label="Endereço" value={dados.endereco_atual} />
        <Field label="Foro" value={dados.foro} />
        <Field label="Cidade da assinatura" value={dados.cidade_assinatura} />
        <Field
          label="Junta Comercial"
          value={
            dados.junta_comercial_sigla && dados.junta_comercial_estado
              ? `${dados.junta_comercial_sigla} — ${dados.junta_comercial_estado}`
              : dados.junta_comercial_sigla || dados.junta_comercial_estado
          }
        />
        <Field label="Início das atividades" value={dados.data_inicio_atividades} />
      </SimpleGrid>
      {dados.objeto_social_atual ? (
        <Box mt={3}>
          <Text fontSize="2xs" color="gray.500" textTransform="uppercase">
            Objeto social
          </Text>
          <Text fontSize="sm" mt={0.5} noOfLines={3}>
            {dados.objeto_social_atual}
          </Text>
        </Box>
      ) : null}
      {dados.cnaes_atual && dados.cnaes_atual.length > 0 ? (
        <Box mt={3}>
          <Text fontSize="2xs" color="gray.500" textTransform="uppercase" mb={1}>
            CNAEs ({dados.cnaes_atual.length})
          </Text>
          <Stack spacing={0.5}>
            {dados.cnaes_atual.slice(0, 5).map(c => (
              <Text key={c.codigo} fontSize="xs" color="gray.700">
                <Text as="span" fontWeight="medium">
                  {c.codigo}
                </Text>{' '}
                — {c.descricao}
              </Text>
            ))}
            {dados.cnaes_atual.length > 5 ? (
              <Text fontSize="2xs" color="gray.500" fontStyle="italic">
                …e mais {dados.cnaes_atual.length - 5}
              </Text>
            ) : null}
          </Stack>
        </Box>
      ) : null}
      {dados.quadro_socios_atual && dados.quadro_socios_atual.length > 0 ? (
        <Box mt={3}>
          <Text fontSize="2xs" color="gray.500" textTransform="uppercase" mb={1}>
            Quadro de sócios ({dados.quadro_socios_atual.length})
          </Text>
          <Stack spacing={1}>
            {dados.quadro_socios_atual.map(s => (
              <HStack
                key={`${s.nome}-${s.cpf || s.cnpj || ''}`}
                fontSize="xs"
                color="gray.700"
                spacing={2}
              >
                <Text fontWeight="medium">{s.nome}</Text>
                {s.cpf ? <Text color="gray.500">CPF {s.cpf}</Text> : null}
                {s.cnpj ? <Text color="gray.500">CNPJ {s.cnpj}</Text> : null}
                {s.percentual != null ? (
                  <Badge colorScheme="purple" fontSize="2xs">
                    {s.percentual}%
                  </Badge>
                ) : null}
              </HStack>
            ))}
          </Stack>
        </Box>
      ) : null}
      <Text fontSize="2xs" color="green.700" mt={3} fontStyle="italic">
        Os campos do passo Empresa serão pré-preenchidos com esses dados quando você clicar em
        Continuar. Você pode editar tudo depois.
      </Text>
    </Box>
  );
};

export const ContratoVigenteStep = ({
  onUploadSuccess,
  dadosExtraidosPersistidos,
}: ContratoVigenteStepProps) => {
  const toast = useToast();
  const showDialog = useDialog();
  const { watch, setValue } = useFormContext<IWizardFormValues>();
  const { iniciarAlteracaoComVigente } = useSocietario();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState<IIniciarAlteracaoResponse | null>(null);

  // Em retomada (refresh do navegador apos upload bem-sucedido), o form
  // value `contrato_id_inicial` ja vem populado mas o estado local nao.
  // Nao tenta re-buscar no backend — MVP fica simples; usuario pode trocar
  // o arquivo se quiser regerar.
  const contratoIdAtual = watch('contrato_id_inicial');

  const handleFile = async (file: File) => {
    if (!file) return;
    if (file.size === 0) {
      toast({ status: 'warning', title: 'Arquivo vazio. Selecione outro.' });
      return;
    }
    // UI promete "ate 50MB" no helper text; sem checagem o backend recebia
    // arquivos arbitrariamente grandes e processava ate consumir memoria.
    if (file.size > MAX_UPLOAD_BYTES) {
      const mb = (file.size / (1024 * 1024)).toFixed(1);
      toast({
        status: 'error',
        title: `Arquivo muito grande (${mb}MB).`,
        description: 'O limite e 50MB. Comprima o PDF ou DOCX antes de tentar de novo.',
      });
      return;
    }
    setUploading(true);
    try {
      const resp = await iniciarAlteracaoComVigente({ file });
      setUploaded(resp);
      setValue('contrato_id_inicial', resp.contrato_id, { shouldValidate: true });
      onUploadSuccess(resp);
      if (resp.extraction_error) {
        toast({
          status: 'warning',
          title: 'Upload feito, mas a extração IA falhou',
          description:
            'Os campos do passo Empresa não serão pré-preenchidos. Você pode digitar manualmente.',
        });
      } else {
        toast({
          status: 'success',
          title: 'Contrato vigente anexado e dados extraídos',
        });
      }
    } catch (err: any) {
      toast({
        status: 'error',
        title: 'Falha ao anexar contrato vigente',
        description: extractApiErrorMessage(err),
      });
    } finally {
      setUploading(false);
    }
  };

  const handleSelectFile = () => fileInputRef.current?.click();

  /**
   * Sessao 2026-05-14 (review Matias PR #345 #13): re-upload do contrato
   * vigente sobrescreve toda a extracao da IA e, em sequencia, o pre-fill do
   * Step Empresa (quando o operador clica em "Continuar" do step). Antes
   * trocava direto sem aviso — perda silenciosa de qualquer edicao manual
   * feita nos passos seguintes. Confirmamos explicitamente.
   */
  const handleTrocarArquivo = () => {
    showDialog({
      status: 'warning',
      title: 'Trocar contrato vigente?',
      description:
        'O arquivo atual será substituído e a IA fará uma nova extração. ' +
        'Os dados extraídos vão sobrescrever o pré-preenchimento dos próximos passos — ' +
        'se você já editou campos manualmente, considere salvar/copiar antes de trocar.',
      confirmLabel: 'Trocar arquivo',
      cancelLabel: 'Cancelar',
      onConfirm: () => fileInputRef.current?.click(),
    });
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  };

  const dragHandlers = {
    onDragOver: (e: React.DragEvent) => e.preventDefault(),
    onDrop: handleDrop,
  };

  // Estado: ja fez upload nesta sessao OU form veio com contrato_id_inicial
  // pre-existente (retomada).
  const jaTemContrato = uploaded !== null || (contratoIdAtual ?? 0) > 0;

  return (
    <VStack align="stretch" spacing={4}>
      <Box>
        <Text fontWeight="bold" fontSize="lg">
          Contrato vigente
        </Text>
        <Text fontSize="sm" color="gray.600" mt={1}>
          Anexe o último contrato (ou alteração) registrado da empresa. A IA vai extrair os dados e
          pré-preencher os próximos passos para você revisar. Aceita PDF ou DOCX.
        </Text>
      </Box>

      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        style={{ display: 'none' }}
        onChange={e => {
          const file = e.target.files?.[0];
          if (file) handleFile(file);
          // Reset pra permitir re-selecionar o mesmo arquivo.
          e.target.value = '';
        }}
      />

      {/* Fix loading (2026-06-17): 3 ramos exclusivos por `&&` (Airbnb proibe
          ternario aninhado). `uploading` vence — cobre upload novo E troca. */}
      {uploading && (
        // o spinner ANTES so existia no ramo de 1o
        // upload (`!jaTemContrato`); na TROCA (jaTemContrato=true) a caixa verde
        // ficava estatica ~30s sem feedback. Içado pro topo: cobre upload novo
        // E troca, com mensagem distinta.
        <Box
          borderWidth="2px"
          borderStyle="dashed"
          borderColor="blue.300"
          borderRadius="md"
          p={8}
          textAlign="center"
          bg="blue.50"
        >
          <VStack spacing={3}>
            <Spinner size="lg" color="blue.500" thickness="3px" />
            <Text fontWeight="medium" color="blue.700">
              {jaTemContrato
                ? 'Reprocessando contrato e extraindo dados…'
                : 'Fazendo upload e extraindo dados…'}
            </Text>
            <Text fontSize="xs" color="gray.500">
              Pode levar até 30 segundos.
            </Text>
          </VStack>
        </Box>
      )}
      {!uploading && !jaTemContrato && (
        <Box
          borderWidth="2px"
          borderStyle="dashed"
          borderColor="gray.300"
          borderRadius="md"
          p={8}
          textAlign="center"
          bg="gray.50"
          cursor="pointer"
          onClick={handleSelectFile}
          {...dragHandlers}
          transition="all 0.2s"
          _hover={{ borderColor: 'blue.400', bg: 'blue.50' }}
        >
          <VStack spacing={2}>
            <Icon as={CloudArrowUp} boxSize={12} color="gray.400" weight="duotone" />
            <Text fontWeight="medium">Arraste o arquivo aqui ou clique para selecionar</Text>
            <Text fontSize="xs" color="gray.500">
              PDF ou DOCX, até 50MB
            </Text>
          </VStack>
        </Box>
      )}
      {!uploading && jaTemContrato && (
        <VStack align="stretch" spacing={3}>
          <Flex
            bg="white"
            borderWidth="1px"
            borderColor="green.200"
            borderRadius="md"
            p={3}
            align="center"
            gap={3}
          >
            <Icon as={FileDoc} boxSize={8} color="green.500" weight="duotone" />
            <Box flex={1}>
              <HStack>
                <Text fontWeight="medium" fontSize="sm">
                  Contrato vigente anexado
                </Text>
                <Icon as={CheckCircle} color="green.500" weight="fill" />
              </HStack>
              <Text fontSize="xs" color="gray.500">
                Contrato #{uploaded?.contrato_id ?? contratoIdAtual}
              </Text>
            </Box>
            <Button size="xs" variant="ghost" onClick={handleTrocarArquivo} isDisabled={uploading}>
              Trocar arquivo
            </Button>
          </Flex>

          {renderUploadFeedback(uploaded, dadosExtraidosPersistidos ?? null)}
        </VStack>
      )}
    </VStack>
  );
};

export default ContratoVigenteStep;
